/**
 * Timeline tab — paginated, filtered event log.
 *
 * Data loaded on-demand from fetchRunEvents(). Filter state managed locally.
 * When the backend is ready, this will call fetchRunEvents() with the active filters.
 */

import { esc } from '../../../shared/rendering.js'
import { fetchRunEvents } from '../../../api/run.js'
import type { ParsedEvent, EventsQuery } from '../../../types/api.js'
import { formatDuration } from '../../../types/domain.js'

// ─── Filter State ───────────────────────────────────────────────────────────

interface TimelineState {
  page: number
  pageSize: number
  order: 'asc' | 'desc'
  errorsOnly: boolean
  search: string
  stage: string | undefined
  eventType: string | undefined
  eventTypeSelect: string | undefined
  tool: string | undefined
  subagent: string | undefined
}

const DEFAULT_STATE: TimelineState = {
  page: 1,
  pageSize: 20,
  order: 'desc',
  errorsOnly: false,
  search: '',
  stage: undefined,
  eventType: undefined,
  eventTypeSelect: undefined,
  tool: undefined,
  subagent: undefined,
}

let _state: TimelineState = { ...DEFAULT_STATE }
let _runId: string | null = null

// ─── Render ─────────────────────────────────────────────────────────────────

/** Render the Timeline tab content. Call with runId to enable data loading. */
export function renderTimelineTab(runId?: string, stages?: string[]): string {
  if (runId) {
    _runId = runId
    _state = { ...DEFAULT_STATE }
  }

  // Schedule async data load after innerHTML is set
  if (_runId) {
    setTimeout(() => {
      void loadEvents()
    }, 0)
  }

  const stageOptions = (stages ?? [])
    .map((s) => `<option value="${esc(s)}">${esc(s)}</option>`)
    .join('')

  return `<div class="tf">
    <div class="tf-row">
      <span class="fp${_state.eventType === undefined ? ' on' : ''}" data-filter="type" data-value="">All</span>
      <span class="fp${_state.eventType === 'tool' ? ' on' : ''}" data-filter="type" data-value="tool">Tools</span>
      <span class="fp${_state.eventType === 'llm' ? ' on' : ''}" data-filter="type" data-value="llm">Messages</span>
      <span class="fp${_state.eventType === 'stage' ? ' on' : ''}" data-filter="type" data-value="stage">Steps</span>
    </div>
    <div class="tf-row">
      <select class="fsel" id="tl-stage"><option value="">All stages</option>${stageOptions}</select>
      <select class="fsel" id="tl-tool"><option value="">All tools</option><option>Edit</option><option>Write</option><option>Bash</option><option>Read</option><option>Grep</option><option>Glob</option></select>
      <span class="fp${_state.errorsOnly ? ' err' : ''}" id="tl-errors">&#9888; Errors</span>
      <input class="fsearch" id="tl-search" placeholder="Search..." value="${esc(_state.search)}">
      <span class="ftog" id="tl-order">${_state.order === 'desc' ? '&#8593; Newest first' : '&#8595; Oldest first'}</span>
    </div>
    <div class="tf-row"><span class="fpg" id="tl-page-info">Page ${_state.page}</span><span class="fp" id="tl-prev">&larr; Prev</span><span class="fp" id="tl-next">Next &rarr;</span></div>
  </div>
  <div class="tl" id="tl-entries"><div class="loading"><div class="spinner"></div></div></div>`
}

// ─── Data Loading ───────────────────────────────────────────────────────────

async function loadEvents(): Promise<void> {
  const container = document.getElementById('tl-entries')
  if (!container || !_runId) {
    return
  }

  container.innerHTML = '<div class="loading"><div class="spinner"></div></div>'

  try {
    const query: Partial<EventsQuery> = {
      page: _state.page,
      page_size: _state.pageSize,
      order: _state.order,
      errors_only: _state.errorsOnly || undefined,
      search: _state.search || undefined,
      stage: _state.stage,
      event_type: _state.eventType ?? _state.eventTypeSelect,
      tool: _state.tool,
      subagent: _state.subagent,
    }
    const result = await fetchRunEvents(_runId, query)
    container.innerHTML = renderEvents(result.data)

    const pageInfo = document.getElementById('tl-page-info')
    if (pageInfo) {
      const total = result.meta?.total ?? result.data.length
      pageInfo.textContent = `Page ${_state.page} (${total} total)`
    }

    wireTimelineEvents()
  } catch (err) {
    container.innerHTML = `<div class="empty">Failed to load events. ${esc(String(err))}</div>`
  }
}

function renderEvents(events: readonly ParsedEvent[]): string {
  if (events.length === 0) {
    return '<div class="empty">No events match the current filters</div>'
  }

  return events
    .map((ev) => {
      const isMod = ev.is_modifying === true
      const phaseClass = getPhaseClass(ev.stage)
      const dur =
        ev.duration_s !== undefined && ev.duration_s !== null
          ? (formatDuration(ev.duration_s) ?? '')
          : ''
      const toolBadges = ev.tool
        ? `<div class="te-tools"><span class="tt${isMod ? ' mod' : ''}">${esc(ev.tool)}</span></div>`
        : ''

      return `<article class="te${isMod ? ' mod' : ''}">
      <div class="te-h">
        <span class="te-num">${ev.iteration !== null ? `Iter ${ev.iteration} \u00b7 ` : ''}${ev.turn !== null ? `Turn ${ev.turn} \u00b7 ` : ''}<span class="te-phase ${phaseClass}">${esc(ev.stage ?? '')}</span></span>
        <span class="te-time">${dur ? `<span class="te-dur">${esc(dur)}</span>` : ''}${esc(ev.timestamp)}</span>
      </div>
      ${toolBadges}
      <div class="te-prev">${esc(ev.summary ?? ev.event_type)}</div>
      ${ev.error ? `<div class="te-err">${esc(ev.error.message)}</div>` : ''}
    </article>`
    })
    .join('')
}

function getPhaseClass(stage: string | null): string {
  if (!stage) {
    return ''
  }
  if (stage === 'implementer') {
    return 'impl'
  }
  if (stage === 'critic' || stage === 'bdd-reviewer') {
    return 'critic'
  }
  if (stage === 'product-manager') {
    return 'pm'
  }
  if (stage === 'reflect' || stage === 'introspect') {
    return 'reflect'
  }
  return ''
}

// ─── Event Wiring ───────────────────────────────────────────────────────────

let _searchTimeout: ReturnType<typeof setTimeout>

function wireTimelineEvents(): void {
  // Filter pills — clears the dropdown so the two controls remain independent
  // (otherwise, clicking "All" pill silently activates the stale dropdown value
  // via the `eventType ?? eventTypeSelect` fallback in the query builder).
  document.querySelectorAll('.tf .fp[data-filter]').forEach((pill) => {
    pill.addEventListener('click', () => {
      const value = (pill as HTMLElement).dataset.value || undefined
      _state.eventType = value
      _state.eventTypeSelect = undefined
      _state.page = 1
      void loadEvents()
    })
  })

  // Error toggle
  document.getElementById('tl-errors')?.addEventListener('click', () => {
    _state.errorsOnly = !_state.errorsOnly
    _state.page = 1
    void loadEvents()
  })

  // Search
  document.getElementById('tl-search')?.addEventListener('input', (e) => {
    _state.search = (e.target as HTMLInputElement).value
    _state.page = 1
    // Debounce: load after 300ms of no typing
    clearTimeout(_searchTimeout)
    _searchTimeout = setTimeout(() => {
      void loadEvents()
    }, 300)
  })

  // Order toggle
  document.getElementById('tl-order')?.addEventListener('click', () => {
    _state.order = _state.order === 'desc' ? 'asc' : 'desc'
    _state.page = 1
    void loadEvents()
  })

  // Pagination
  document.getElementById('tl-prev')?.addEventListener('click', () => {
    if (_state.page > 1) {
      _state.page--
      void loadEvents()
    }
  })
  document.getElementById('tl-next')?.addEventListener('click', () => {
    _state.page++
    void loadEvents()
  })

  // Stage select
  document.getElementById('tl-stage')?.addEventListener('change', (e) => {
    _state.stage = (e.target as HTMLSelectElement).value || undefined
    _state.page = 1
    void loadEvents()
  })

  // Tool select
  document.getElementById('tl-tool')?.addEventListener('change', (e) => {
    _state.tool = (e.target as HTMLSelectElement).value || undefined
    _state.page = 1
    void loadEvents()
  })

  // Agent select
  document.getElementById('tl-agent')?.addEventListener('change', (e) => {
    _state.subagent = (e.target as HTMLSelectElement).value || undefined
    _state.page = 1
    void loadEvents()
  })

  // Event type select — clears the pill selection so the two controls remain independent.
  document.getElementById('tl-event-type')?.addEventListener('change', (e) => {
    _state.eventTypeSelect = (e.target as HTMLSelectElement).value || undefined
    _state.eventType = undefined
    _state.page = 1
    void loadEvents()
  })
}
